/**
 * lab-cloud answers some refusals with a sentence written for the person using the app — a locked
 * order, an analysis that cannot be combined with another on one sample. Those two codes carry
 * their reason in `message`; every other code's message is for developers (often English) and must
 * not reach the screen. The apiClient never throws, so the store turns the result into this error
 * and the modal asks it for the reason, instead of showing a generic "try again" that hides why a
 * save was refused (MIL-552).
 *
 * `message` stays the bare code, as it is for every other store error, so callers that branch on
 * `error.message === 'already-exists'` keep working.
 */
const USER_FACING_REFUSAL_CODES = ['invalid-params', 'forbidden']

export class ApiRefusalError extends Error {
  readonly code: string
  readonly userMessage: string | null

  constructor(code: string, userMessage: string | null) {
    super(code)
    this.name = 'ApiRefusalError'
    this.code = code
    this.userMessage = userMessage
  }
}

/** Builds the error to throw for a failed apiClient result. */
export function toRefusalError(result: { code: string; message?: string | null }): ApiRefusalError {
  const isUserFacing = USER_FACING_REFUSAL_CODES.includes(result.code)
  return new ApiRefusalError(result.code, isUserFacing && result.message ? result.message : null)
}

/** The sentence to show the user for a thrown error, or null when there is none to show. */
export function getRefusalReason(error: unknown): string | null {
  return error instanceof ApiRefusalError ? error.userMessage : null
}
